import { Link, usePage } from "@inertiajs/react"
import { Settings } from "lucide-react"
import { cn } from "@/lib/utils"

// The left column of every Email screen — the configuration link, then each
// template with a dot: green when the admin's own wording is in use, grey
// when the built-in text is.
export type EmailNavProps = {
  settings_href: string
  templates: { key: string; label: string; href: string; custom: boolean }[]
}

export function EmailNav({ nav }: { nav: EmailNavProps }) {
  const url = usePage().url
  const active = (href: string) => url.split("?")[0] === href

  const item = (href: string, label: string, extra?: React.ReactNode) => (
    <Link
      key={href}
      href={href}
      className={cn(
        "flex items-center justify-between gap-2 border-l-2 px-4 py-2.5 text-sm no-underline transition-colors",
        active(href)
          ? "border-accent bg-accent/5 text-ink-display"
          : "border-transparent text-ink-body hover:bg-page",
      )}
    >
      <span className="flex items-center gap-2">{label}</span>
      {extra}
    </Link>
  )

  return (
    <nav aria-label="Email settings" className="overflow-hidden rounded-lg border border-hairline bg-white">
      <p className="px-4 pb-1 pt-4 text-[10px] font-semibold uppercase tracking-widest text-ink-muted">
        Email settings
      </p>
      {item(
        nav.settings_href,
        "Email configuration",
        <Settings className="h-4 w-4 text-ink-muted" aria-hidden="true" />,
      )}
      <p className="px-4 pb-1 pt-4 text-[10px] font-semibold uppercase tracking-widest text-ink-muted">
        Email templates
      </p>
      <div className="pb-2">
        {nav.templates.map((t) =>
          item(
            t.href,
            t.label,
            <span
              className={cn("h-2 w-2 shrink-0 rounded-full", t.custom ? "bg-accent" : "bg-ink-muted/30")}
              title={t.custom ? "Custom wording in use" : "Default wording"}
              aria-label={t.custom ? "Custom wording in use" : "Default wording"}
            />,
          ),
        )}
      </div>
    </nav>
  )
}

export function EmailLayout({ nav, children }: { nav: EmailNavProps; children: React.ReactNode }) {
  return (
    <div className="mt-6 grid gap-6 lg:grid-cols-[16rem_1fr]">
      <div className="lg:sticky lg:top-6 lg:self-start">
        <EmailNav nav={nav} />
      </div>
      <div className="min-w-0">{children}</div>
    </div>
  )
}
